import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

// API should include /api prefix for backend calls
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

/**
 * Async analysis hook - uses polling-based approach for long-running analyses
 * Starts analysis via /analyze/start, polls /analyze/status/{job_id} for progress
 */
export function useAnalysisWebSocket() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [progressStep, setProgressStep] = useState('');
  
  const pollingRef = useRef(null);
  const jobIdRef = useRef(null);
  const foundRef = useRef(false);

  // Stop polling
  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    jobIdRef.current = null;
  }, []);

  // Reset state
  const reset = useCallback(() => {
    cleanup();
    setIsAnalyzing(false);
    setAnalysis(null);
    setError(null);
    setProgress(0);
    setProgressText('');
    setProgressStep('');
    foundRef.current = false;
  }, [cleanup]);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId) => {
    const token = localStorage.getItem('auth_token');
    
    try {
      const statusResponse = await axios.get(`${API}/analyze/status/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const status = statusResponse.data;
      
      // Update progress
      setProgress(status.progress || 0);
      setProgressText(status.progress_message || '');
      setProgressStep(status.progress_step || '');
      
      if (status.status === 'completed') {
        // Fetch the full result
        const resultResponse = await axios.get(`${API}/analyze/result/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        foundRef.current = true;
        setAnalysis(resultResponse.data);
        setIsAnalyzing(false);
        cleanup();
        return true;
      } else if (status.status === 'failed') {
        foundRef.current = true;
        setError(status.error_message || 'Analyse fehlgeschlagen');
        setIsAnalyzing(false);
        cleanup();
        return true;
      }
      
      return false; // Still processing
    } catch (e) {
      console.error('Status poll error:', e);
      return false;
    }
  }, [cleanup]);

  // Start analysis
  const startAnalysis = useCallback((analysisRequest) => {
    return new Promise(async (resolve, reject) => {
      reset();
      setIsAnalyzing(true);
      setProgressText('Starte Analyse...');
      foundRef.current = false;
      
      const token = localStorage.getItem('auth_token');
      
      try {
        // Start the async analysis
        const startResponse = await axios.post(`${API}/analyze/start`, analysisRequest, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const jobId = startResponse.data.job_id;
        jobIdRef.current = jobId;
        
        console.log('Analysis job started:', jobId);
        setProgressText(startResponse.data.message || 'Analyse gestartet...');
        
        // Start polling for status
        pollingRef.current = setInterval(async () => {
          if (foundRef.current) {
            cleanup();
            return;
          }
          
          const completed = await pollJobStatus(jobId);
          if (completed && foundRef.current) {
            // Get analysis from state won't work immediately, so we poll result directly
            try {
              const resultResponse = await axios.get(`${API}/analyze/result/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              resolve(resultResponse.data);
            } catch (e) {
              // If failed, reject
              reject(new Error('Analyse fehlgeschlagen'));
            }
          }
        }, 2000); // Poll every 2 seconds
        
        // Timeout after 5 minutes
        setTimeout(() => {
          if (!foundRef.current) {
            setError('Analyse-Timeout. Bitte prüfen Sie den Verlauf.');
            setIsAnalyzing(false);
            cleanup();
            reject(new Error('Timeout'));
          }
        }, 300000);
        
      } catch (e) {
        console.error('Failed to start analysis:', e);
        const errorMsg = e.response?.data?.detail || 'Analyse konnte nicht gestartet werden';
        setError(errorMsg);
        setIsAnalyzing(false);
        reject(new Error(errorMsg));
      }
    });
  }, [reset, cleanup, pollJobStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return {
    isAnalyzing,
    analysis,
    error,
    startAnalysis,
    reset,
    setAnalysis,
    setError,
    setIsAnalyzing,
    progress,
    progressText,
    progressStep,
    progressDetails: {},
    isConnected: false
  };
}

export default useAnalysisWebSocket;
